import { useState } from "react";
import PageHeader from "../../components/common/PageHeader";
import ShadowContainer from "../../components/common/ShadowContainer";
import Pagination from "../../components/Pagination";
import { useFeedbacks } from "./hooks/useFeedbacks";

/* ─────────────────────────────────────────
   Small reusable sub-components
───────────────────────────────────────── */

const STAR_COLOR = {
  high: "text-green-500",   // 5
  mid: "text-blue-500",     // 3–4
  low: "text-red-500",      // 1–2
};

const Stars = ({ value }) => {
  const color =
    value >= 5 ? STAR_COLOR.high : value >= 3 ? STAR_COLOR.mid : STAR_COLOR.low;
  return (
    <span className={`text-sm ${color}`}>
      {[1, 2, 3, 4, 5].map((s) => (
        <span key={s}>{s <= value ? "★" : "☆"}</span>
      ))}
      <span className="ml-1 text-gray-400 text-xs">({value})</span>
    </span>
  );
};

const ExpandableComment = ({ comment }) => {
  const [expanded, setExpanded] = useState(false);
  const LIMIT = 60;

  if (!comment) return <span className="text-gray-400 text-xs">—</span>;

  const needsTruncation = comment.length > LIMIT;
  const displayText =
    expanded || !needsTruncation ? comment : comment.slice(0, LIMIT) + "…";

  return (
    <span className="text-sm text-gray-700">
      {displayText}
      {needsTruncation && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((prev) => !prev);
          }}
          className="ml-1 text-blue-600 text-xs hover:underline"
        >
          {expanded ? "Less" : "More"}
        </button>
      )}
    </span>
  );
};

const ItemRatings = ({ items }) => {
  if (!items?.length) return <span className="text-gray-400 text-xs">—</span>;
  return (
    <div className="space-y-1.5 min-w-[200px]">
      {items.map((item, i) => (
        <div key={i} className="flex items-center justify-between gap-4">
          <span className="text-xs font-medium text-gray-700 truncate flex-1">
            {item.productTitle}
          </span>
          <span className="flex-shrink-0">
            <Stars value={item.rating} />
          </span>
        </div>
      ))}
    </div>
  );
};

const ColFilter = ({ value, onChange, placeholder }) => (
  <input
    type="text"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    className="mt-1 w-full px-2 py-1 text-xs font-normal border border-gray-200 rounded-md bg-white focus:outline-none focus:border-gray-400"
  />
);

const RatingFilter = ({ value, onChange }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="mt-1 w-full px-2 py-1 text-xs font-normal border border-gray-200 rounded-md bg-white focus:outline-none focus:border-gray-400"
  >
    <option value="">All</option>
    {[1, 2, 3, 4, 5].map((v) => (
      <option key={v} value={v}>
        {v} Star{v > 1 ? "s" : ""}
      </option>
    ))}
  </select>
);

/* ─────────────────────────────────────────
   Column definitions
   noFilter: true  → no search input
   type: "rating"  → dropdown filter
───────────────────────────────────────── */
const COLUMNS = [
  { key: "orderId",             label: "Order ID",      placeholder: "Search ID…" },
  { key: "username",            label: "User Name",     placeholder: "Search name…" },
  { key: "phoneNumber",         label: "Phone",         placeholder: "Search phone…" },
  { key: "feedbackDate",        label: "Date",          noFilter: true },
  { key: "overallRating",       label: "Overall",       type: "rating" },
  { key: "comment",             label: "Comment",       placeholder: "Search comment…" },
  { key: "joinedProductTitles", label: "Item Ratings",  placeholder: "Search product…" },
];

/* ─────────────────────────────────────────
   Main page
───────────────────────────────────────── */
export default function UserFeedback() {
  const {
    feedbacks,
    loading,
    page,
    totalPages,
    filters,
    activeFilterCount,
    setPage,
    setFilter,
    clearFilters,
  } = useFeedbacks();

  return (
    <div className="p-4 md:p-6 space-y-4">
      <PageHeader
        title="User Feedback"
        subtitle="Customer reviews and ratings"
        showBackButton={true}
        action={
          activeFilterCount > 0 ? (
            <button
              onClick={clearFilters}
              className="text-xs text-gray-500 border border-gray-200 px-3 py-1.5 rounded-lg hover:border-gray-400 hover:text-black transition-all"
            >
              Clear filters ({activeFilterCount})
            </button>
          ) : null
        }
      />

      <ShadowContainer maxWidth="w-full" padding="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {COLUMNS.map(({ key, label, placeholder, noFilter, type }) => (
                  <th
                    key={key}
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide"
                  >
                    {label}
                    {!noFilter &&
                      (type === "rating" ? (
                        <RatingFilter
                          value={filters.overallRating}
                          onChange={(v) => setFilter("overallRating", v)}
                        />
                      ) : (
                        <ColFilter
                          value={filters[key] || ""}
                          onChange={(v) => setFilter(key, v)}
                          placeholder={placeholder}
                        />
                      ))}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td
                    colSpan={COLUMNS.length}
                    className="px-4 py-12 text-center text-gray-400"
                  >
                    Loading…
                  </td>
                </tr>
              ) : feedbacks.length === 0 ? (
                <tr>
                  <td
                    colSpan={COLUMNS.length}
                    className="px-4 py-12 text-center text-gray-400"
                  >
                    No feedback found
                  </td>
                </tr>
              ) : (
                feedbacks.map((fb) => (
                  <tr
                    key={fb._id}
                    className="hover:bg-blue-50 transition-colors"
                  >
                    <td className="px-4 py-3 font-mono text-xs text-gray-600">
                      {fb.orderId}
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-800">
                      {fb.username}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {fb.phoneNumber}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                      {fb.feedbackDate}
                    </td>
                    <td className="px-4 py-3">
                      <Stars value={fb.overallRating} />
                    </td>
                    <td className="px-4 py-3 max-w-[200px]">
                      <ExpandableComment comment={fb.comment} />
                    </td>
                    <td className="px-4 py-3">
                      <ItemRatings items={fb.itemRatings} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Common Pagination uses 1-based pages; hook uses 0-based */}
        <Pagination
          currentPage={page + 1}
          totalPages={totalPages}
          onPageChange={(p) => setPage(p - 1)}
        />
      </ShadowContainer>
    </div>
  );
}
